<template>
  <div class="space-y-6">
    <h2 class="text-xl font-semibold">{{ t('print.delivery-method') }}</h2>
    <p class="text-sm text-gray -mt-2">
      Processing and shipping times may vary depending on the type and quantity
      of items in your order.
    </p>
    <div v-if="loading" class="bg-white rounded-lg p-4 lg:p-6 shadow-sm">
      <p class="text-gray">Loading shipping rates...</p>
    </div>

    <div v-else-if="error" class="bg-white rounded-lg p-4 lg:p-6 shadow-sm">
      <p class="text-red">{{ error }}</p>
    </div>

    <div v-else-if="shippingRates.length > 0" class="space-y-4">
      <div
        v-for="rate in shippingRates"
        :key="rate.serviceType"
        class="p-3 lg:p-4 border rounded-lg cursor-pointer"
        :class="
          selectedRate?.serviceType === rate.serviceType
            ? 'border-primary bg-primary/5'
            : 'border-gray-200'
        "
        @click="selectRate(rate)"
      >
        <div class="flex justify-between items-center">
          <div>
            <h3 class="font-medium">Total Shipping Cost</h3>
          </div>
          <div class="text-base lg:text-lg font-medium">
            <template v-if="showFreeShipping">
              Free
              <div
                v-if="businessShippingConfig?.reason"
                class="text-xs text-gray-500 font-normal"
              >
                {{ businessShippingConfig.reason }}
              </div>
            </template>
            <template
              v-else-if="
                printStore.checkout.shippingAddress?.address?.country ===
                  'US' && rate.price > 19.99
              "
            >
              {{ printStore.currencySymbol }}19.99
            </template>
            <template v-else>
              {{ printStore.currencySymbol }}{{ rate.price.toFixed(2) }}
            </template>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="bg-white rounded-lg p-4 lg:p-6 shadow-sm">
      <p class="text-gray">No shipping rates available</p>
    </div>

    <div class="flex justify-between pt-4">
      <button
        type="button"
        class="px-4 py-2 lg:px-6 text-gray hover:text-gray"
        @click="$emit('back')"
      >
        {{ t('print.back') }}
      </button>
      <button
        type="button"
        class="px-4 py-2 lg:px-6 bg-primary text-[white] rounded-lg hover:bg-primary-dark transition-colors"
        :disabled="loading || (!selectedRate && !error)"
        @click="handleContinue"
      >
        {{ t('print.continue-to-payment') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePrintStore } from '~/store/print';
import {
  apiGetShippingRates,
  apiGetBusinessShippingConfig,
  type ShippingRate,
  type BusinessShippingConfigResponse,
} from '~/api/print';
import { useOrganizationStore } from '~/store/organization';

const { t } = useI18n();
const printStore = usePrintStore();
const organizationStore = useOrganizationStore();
const emit = defineEmits<{
  (e: 'next'): void;
  (e: 'back'): void;
}>();

const loading = ref(false);
const error = ref<string | null>(null);
const shippingRates = ref<ShippingRate[]>([]);
const selectedRate = ref<ShippingRate | null>(null);
const businessShippingConfig = ref<BusinessShippingConfigResponse | null>(null);

const isFullyFinanced = computed(() => {
  const result =
    (organizationStore.organization as any)?.fullyFinancedPrint ?? false;
  return result;
});

const hasBusinessFreeShipping = computed(() => {
  const result = businessShippingConfig.value?.hasFreeShipping ?? false;
  return result;
});

const showFreeShipping = computed(() => {
  if (shippingRates.value.length > 0 && shippingRates.value[0].price > 0) {
    return false;
  }

  const result = isFullyFinanced.value || hasBusinessFreeShipping.value;
  return result;
});

const calculateFinalShippingPrice = (originalPrice: number): number => {
  if (showFreeShipping.value) {
    return 0;
  }

  const isUS = printStore.checkout.shippingAddress?.address?.country === 'US';
  const needsCapping = isUS && originalPrice > 19.99;
  const finalPrice = needsCapping ? 19.99 : originalPrice;
  return finalPrice;
};

const setShippingRateWithPricing = (rate: ShippingRate) => {
  const finalPrice = calculateFinalShippingPrice(rate.price);

  const shippingRateData = {
    service: rate.serviceName,
    amount: finalPrice,
    estimatedDays: rate.transitDays,
  };
  printStore.setShippingRate(shippingRateData);
};

const getDefaultShippingOption = (): ShippingRate => {
  const isOutsideUS =
    printStore.checkout.shippingAddress?.address.country !== 'US';
  const currency = printStore.checkout.currency || printStore.orgCurrency;

  if (isOutsideUS && (currency === 'GBP' || currency === 'EUR')) {
    return {
      serviceType: 'INTERNATIONAL',
      serviceName: 'Parcelforce',
      transitDays: '1 Business Day (excludes print time)',
      price: 13.95,
    };
  }

  if (isOutsideUS && currency === 'CAD') {
    return {
      serviceType: 'INTERNATIONAL',
      serviceName: 'Canada Post',
      transitDays: '3-5 Business Days (excludes print time)',
      price: 18.99,
    };
  }

  return {
    serviceType: 'GROUND',
    serviceName: 'Standard Shipping',
    transitDays: '5-7 Business Days',
    price: 19.99,
  };
};

const calculateTotalWeight = () => {
  let totalWeight = 0;
  for (const [key, item] of Object.entries(printStore.selectedItems)) {
    const quantity = printStore.quantities[key] || 0;
    if (!item.printProduct.weightOz) {
      continue;
    }
    const itemWeightLbs = item.printProduct.weightOz / 16;
    const itemTotalWeight = quantity * itemWeightLbs;

    totalWeight += itemTotalWeight;
  }

  const finalWeight = Math.max(totalWeight, 0.1);

  return finalWeight;
};

const prepareItemsForShipping = () => {
  const items = [];

  for (const [key, item] of Object.entries(printStore.selectedItems)) {
    const quantity = printStore.quantities[key] || 0;
    if (quantity > 0) {
      items.push({
        id: key,
        weight: item.printProduct.weightOz
          ? item.printProduct.weightOz / 16
          : 0.1, // Convert oz to lbs
        quantity,
      });
    }
  }

  return items;
};

onMounted(async () => {
  if (!printStore.checkout.currency) {
    printStore.initCurrency();
  }

  printStore.syncCurrencyWithAddress();

  businessShippingConfig.value = await apiGetBusinessShippingConfig();
  const currentCurrency = printStore.checkout.currency;

  if (!printStore.checkout.shippingAddress?.address.postal_code) {
    error.value =
      'Missing postal code in shipping address. Using default shipping option.';
    const defaultOption = getDefaultShippingOption();
    shippingRates.value = [defaultOption];
    selectedRate.value = defaultOption;
    setShippingRateWithPricing(defaultOption);
    return;
  }

  loading.value = true;
  error.value = '';

  try {
    const isOutsideUS =
      printStore.checkout.shippingAddress?.address.country !== 'US';
    const useFlatRate =
      isOutsideUS &&
      (printStore.checkout.currency === 'GBP' ||
        printStore.checkout.currency === 'EUR' ||
        printStore.checkout.currency === 'CAD');

    if (Object.keys(printStore.selectedItems).length === 0 || useFlatRate) {
      const defaultOption = getDefaultShippingOption();

      error.value =
        Object.keys(printStore.selectedItems).length === 0
          ? 'No items selected. Using default shipping option.'
          : '';

      shippingRates.value = [defaultOption];
      selectedRate.value = defaultOption;
      setShippingRateWithPricing(defaultOption);
      loading.value = false;
      return;
    }

    const items = prepareItemsForShipping();
    const contentFilePrintIds = Object.keys(printStore.selectedItems).map(
      (key) => Number.parseInt(key, 10),
    );
    const totalWeight = calculateTotalWeight();

    const apiPayload = {
      toAddress: {
        postalCode: printStore.checkout.shippingAddress.address.postal_code,
        country: printStore.checkout.shippingAddress.address.country || 'US',
      },
      weight: totalWeight,
      items,
      contentFilePrintIds,
    };
    const response = await apiGetShippingRates(apiPayload);

    const { rates } = response;

    const origCurrency = printStore.orgCurrency;
    const currentCurrency = printStore.checkout.currency;

    shippingRates.value =
      origCurrency === currentCurrency
        ? rates
        : rates.map((rate) => ({
            ...rate,
            price: printStore.convertPrice(rate.price),
          }));

    if (shippingRates.value.length > 0) {
      const [firstRate] = shippingRates.value;
      selectedRate.value = firstRate;
      setShippingRateWithPricing(firstRate);
    } else {
      const defaultOption = getDefaultShippingOption();
      shippingRates.value = [defaultOption];
      selectedRate.value = defaultOption;
      setShippingRateWithPricing(defaultOption);
    }
  } catch {
    error.value =
      'Failed to fetch shipping rates. Using default shipping option.';

    const defaultOption = getDefaultShippingOption();
    shippingRates.value = [defaultOption];
    selectedRate.value = defaultOption;
    setShippingRateWithPricing(defaultOption);
  } finally {
    loading.value = false;
  }
});

const selectRate = (rate: ShippingRate) => {
  selectedRate.value = rate;
  setShippingRateWithPricing(rate);
};

const handleContinue = () => {
  if (selectedRate.value) {
    emit('next');
  }
};
</script>
